import { createApi, retry } from "@reduxjs/toolkit/query/react";
import camelCase from "lodash/camelCase";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import snakeCase from "lodash/snakeCase";

import { TAB_ID, TAB_ID_HEADER } from "App/main/Utils";
import { deepCaseConversion, waitForBackoffAndJitter } from "Services/HelpersService";
import { getLogger } from "Services/LoggingService";
import { fetch } from "Services/util/fetchPonyfill";

import { apiHeaders, type ApiHeaders } from "./apiHeaders";
import { isUiHttpError, UiHttpError } from "./uiHttpError";

import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import type { ApiRequestParams } from "Services/AbstractApiService";

const logger = getLogger("UiRestClient");

export type UiRestClientConfig = {
    host: string;
    getHeaders?: () => Record<string, string>;
    performCaseConversion?: boolean;
    performResponseCaseConversion?: boolean;
    credentials?: RequestCredentials;
};

export type HostRestApiQueryArg = ApiRequestParams;

export type AsHostRestApiOptions = {
    reducerPath: string;
};

export type UiRestRequest = {
    endpoint: string;
    body?: Record<string, unknown> | FormData;
    headers?: Record<string, string>;
    method?: "PATCH" | "POST" | "PUT" | "DELETE";
    signal?: AbortSignal;
    retryAttempts?: number;
    currentRetry?: number;
    performCaseConversion?: boolean;
    performResponseCaseConversion?: boolean;
    credentials?: RequestCredentials;
};

async function readResponseBody(response: Response): Promise<unknown> {
    const contentType = response.headers.get("Content-Type") ?? "";
    if (response.status === 204) {
        return undefined;
    }

    if (contentType === "text/csv") {
        return response;
    }

    const text = await response.text();
    if (!text) {
        return undefined;
    }

    try {
        return JSON.parse(text) as unknown;
    } catch {
        return text;
    }
}

export class UiRestClient {
    constructor(private readonly config: UiRestClientConfig) {}

    async requestJson<T>({
        endpoint,
        body,
        headers,
        method,
        signal,
        retryAttempts,
        currentRetry = 0,
        performCaseConversion = this.config.performCaseConversion ?? false,
        performResponseCaseConversion = this.config.performResponseCaseConversion ?? true,
        credentials = this.config.credentials ?? "same-origin",
    }: UiRestRequest): Promise<T> {
        const url = `${this.config.host}${endpoint}`;
        const computedHeaders = {
            ...(body instanceof FormData ? {} : { "Content-Type": "application/json" }),
            ...(headers ?? this.config.getHeaders?.() ?? apiHeaders()),
            [TAB_ID_HEADER]: TAB_ID,
        };

        const requestInit: RequestInit = {
            headers: computedHeaders,
            credentials,
            ...(signal ? { signal } : {}),
        };

        const hasBody =
            body != null && (body instanceof FormData ? body.entries().next().done === false : !isEmpty(body));

        if (method != null || hasBody) {
            requestInit.method = method ?? "POST";
            requestInit.body =
                body instanceof FormData
                    ? body
                    : JSON.stringify(performCaseConversion ? deepCaseConversion(body, snakeCase) : body);
        }

        const httpMethod = requestInit.method ?? "GET";

        try {
            const response = await fetch(url, requestInit);

            if (response.ok) {
                if (headers?.["Content-Type"] === "text/csv") {
                    return response as unknown as T;
                }

                const json = await readResponseBody(response);
                return (
                    performResponseCaseConversion && json !== undefined ? deepCaseConversion(json, camelCase) : json
                ) as T;
            }

            const errorBody = await readResponseBody(response);
            throw new UiHttpError("Request failed", {
                kind: "http",
                url,
                method: httpMethod,
                statusCode: response.status,
                body: errorBody,
            });
        } catch (error) {
            if (error instanceof Error && error.name === "AbortError") {
                throw error;
            }

            if (error instanceof UiHttpError) {
                throw error;
            }

            if (error instanceof Error) {
                const isRequestError = error.message?.indexOf("Failed to fetch") >= 0;
                logger.warn(`Fetch Error for endpoint`, { endpoint, requestError: error, isRequestError });

                if (isRequestError && retryAttempts && currentRetry < retryAttempts) {
                    await waitForBackoffAndJitter(currentRetry);
                    return this.requestJson<T>({
                        endpoint,
                        body,
                        headers,
                        method,
                        signal,
                        retryAttempts,
                        currentRetry: currentRetry + 1,
                        performCaseConversion,
                        performResponseCaseConversion,
                        credentials,
                    });
                }

                throw new UiHttpError("Request failed", {
                    kind: isRequestError ? "network" : "http",
                    url,
                    method: httpMethod,
                    body: undefined,
                });
            }

            throw error;
        }
    }

    /**
     * RTK Query baseQuery backed by this client.
     * Merges {@link BaseQueryApi.signal} for abort; do not pass `signal` from endpoint `query` callbacks.
     */
    private createRestApiBaseQuery(): BaseQueryFn<HostRestApiQueryArg, unknown, UiHttpError> {
        return async (arg, api) => {
            const requestParams = omit(arg, "signal");

            try {
                const data = await this.requestJson({
                    ...requestParams,
                    signal: api.signal,
                });
                return { data };
            } catch (error) {
                if (isUiHttpError(error)) {
                    return { error };
                }
                throw error;
            }
        };
    }

    /**
     * Creates a per-host RTK Query slice using this client as transport.
     * Defaults: no RTK retries (`maxRetries: 0`); network retry via `retryAttempts` on {@link ApiRequestParams}.
     */
    asHostRestApi({ reducerPath }: AsHostRestApiOptions) {
        return createApi({
            reducerPath,
            baseQuery: retry(this.createRestApiBaseQuery(), { maxRetries: 0 }),
            refetchOnFocus: false,
            refetchOnReconnect: true,
            keepUnusedDataFor: 60,
            endpoints: () => ({}),
        });
    }
}

export function createUiRestClient(config: UiRestClientConfig): UiRestClient {
    return new UiRestClient({
        getHeaders: apiHeaders,
        ...config,
    });
}

export type { ApiHeaders };
